import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Grid,
  Card,
  Text,
  Avatar,
  Input,
  Checkbox,
  Pagination,
  Row,
  Col,
  Spacer,
  Loading,
  Button,
  Popover,
} from '@nextui-org/react';
import { FaSearch, FaComment } from 'react-icons/fa';

// Mock Data Types
interface User {
  id: string;
  name: string;
  status: 'online' | 'offline';
  systemStatus: 'logged_in' | 'logged_out';
  activityStatus: 'idle' | 'active' | 'in-session';
  group: string;
  browser: string;
  os: string;
  unreadMessages: number;
}

// Mock Data Generator
const GROUPS = Array.from({ length: 90 }, (_, i) => `Group ${i + 1}`);
const BROWSERS = ['Chrome', 'Firefox', 'Safari', 'Edge'];
const OS = ['Windows 10', 'macOS', 'Linux', 'iOS', 'Android'];

const generateUsers = (): User[] => {
  const users: User[] = [];
  let userIndex = 0;

  for (const group of GROUPS) {
    const usersInGroup = Math.floor(Math.random() * 3) + 1; // 1 to 3 users per group
    for (let i = 0; i < usersInGroup; i++) {
      users.push({
        id: `user-${userIndex}`,
        name: `User ${userIndex + 1}`,
        status: Math.random() > 0.5 ? 'online' : 'offline',
        systemStatus: Math.random() > 0.3 ? 'logged_in' : 'logged_out',
        activityStatus: ['idle', 'active', 'in-session'][Math.floor(Math.random() * 3)] as 'idle' | 'active' | 'in-session',
        group,
        browser: BROWSERS[Math.floor(Math.random() * BROWSERS.length)],
        os: OS[Math.floor(Math.random() * OS.length)],
        unreadMessages: Math.random() > 0.7 ? Math.floor(Math.random() * 5) + 1 : 0,
      });
      userIndex++;
    }
  }
  return users;
};

const UserMonitoringPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeMonitors, setActiveMonitors] = useState(0);

  // Filters
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setUsers(generateUsers());
      setActiveMonitors(Math.floor(Math.random() * 10) + 1); // Mock active monitors count
      setLoading(false);
    }, 500);
  }, []);

  // Simulate real-time updates
  useEffect(() => {
    if (loading) return;

    const interval = setInterval(() => {
      setUsers(prevUsers =>
        prevUsers.map(user => {
          // Randomly update some users to simulate real-time activity
          if (Math.random() > 0.9) {
            return {
              ...user,
              status: Math.random() > 0.5 ? 'online' : 'offline',
              systemStatus: Math.random() > 0.3 ? 'logged_in' : 'logged_out',
              activityStatus: ['idle', 'active', 'in-session'][Math.floor(Math.random() * 3)] as 'idle' | 'active' | 'in-session',
              unreadMessages: Math.random() > 0.8 ? user.unreadMessages + 1 : user.unreadMessages,
            };
          }
          return user;
        })
      );
    }, 3000); // Update every 3 seconds

    return () => clearInterval(interval);
  }, [loading]);

  // Filter Logic
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesGroup = selectedGroups.length === 0 || selectedGroups.includes(user.group);
      const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesGroup && matchesSearch;
    });
  }, [users, selectedGroups, searchQuery]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedGroups, searchQuery]);

  // Group the filtered users by their group name
  const groupedUsers = useMemo(() => {
    const groups: Record<string, User[]> = {};
    filteredUsers.forEach(user => {
      if (!groups[user.group]) {
        groups[user.group] = [];
      }
      groups[user.group].push(user);
    });
    return groups;
  }, [filteredUsers]);

  const groupNames = Object.keys(groupedUsers);

  // Pagination Logic (paginating by group instead of individual users)
  const itemsPerPage = 100; // e.g., show 10 groups per page
  const totalPages = Math.ceil(groupNames.length / itemsPerPage);
  const currentGroups = groupNames.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <Container display="flex" justify="center" alignItems="center" css={{ height: '100vh' }}>
        <Loading size="xl" />
      </Container>
    );
  }

  return (
    <Container fluid css={{ padding: '20px' }}>
      {/* Main Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* Header */}
        <Row justify="space-between" align="center" wrap="wrap">
          <Col css={{ width: 'auto' }}>
            <Text h2>User Monitoring</Text>
          </Col>
          <Col css={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '20px' }}>
            <Text b>Active Monitors: {activeMonitors}</Text>
            <Text color="gray">
              Environment: {import.meta.env.VITE_APP_ENV || 'Development'}
            </Text>
          </Col>
        </Row>
        <Spacer y={1} />

        {/* Filters & Search */}
        <Card css={{ padding: "$8" }}>
          <Row justify="space-between" align="center" wrap="wrap">
            <Col css={{ mw: "400px", marginBottom: "10px" }}>
              <Input
                clearable
                contentLeft={<FaSearch />}
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
              />
            </Col>
            <Col css={{ width: "auto" }}>
              <Checkbox.Group
                orientation="horizontal"
                label="Filter by Group"
                value={selectedGroups}
                onChange={setSelectedGroups}
              >
                {GROUPS.map(group => (
                  <Checkbox key={group} value={group}>{group}</Checkbox>
                ))}
              </Checkbox.Group>
            </Col>
          </Row>
        </Card>

        <Spacer y={1} />

        {/* Groups Display */}
        <Grid.Container gap={1} justify="flex-start">
          {currentGroups.map(groupName => (
            <Grid xs={12} sm={6} md={4} lg={3} xl={2} key={groupName}>
              <Card isHoverable variant="bordered" css={{ padding: 0, overflow: 'visible', width: '100%' }}>
                <Card.Header css={{ padding: '$2', paddingBottom: 0, justifyContent: 'center' }}>
                  <Text b size="$sm">{groupName}</Text>
                </Card.Header>
                <Card.Body css={{ padding: '$2', flexDirection: 'row', flexWrap: 'wrap', gap: '$2', justifyContent: 'center', overflow: 'visible' }}>
                  {groupedUsers[groupName].map(user => (
                    <Popover key={user.id} placement="top">
                      <Popover.Trigger>
                        <div style={{ position: 'relative', cursor: 'pointer', margin: '2px' }}>
                          <Avatar
                            text={user.name.charAt(0)}
                            size="md"
                            bordered
                            color={user.status === 'online' ? 'success' : 'default'}
                            css={{ margin: 0 }}
                          />
                          {user.unreadMessages > 0 && (
                            <div style={{ position: 'absolute', top: -6, right: -6, background: 'red', color: 'white', borderRadius: '50%', width: 16, height: 16, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                              {user.unreadMessages}
                            </div>
                          )}
                          <div style={{ position: 'absolute', bottom: -2, right: -2, background: user.activityStatus === 'in-session' ? '#9750dd' : user.activityStatus === 'active' ? '#0072F5' : '#979797', borderRadius: '50%', width: 12, height: 12, border: '2px solid white', zIndex: 10 }} title={`Activity: ${user.activityStatus}`} />
                        </div>
                      </Popover.Trigger>
                      <Popover.Content css={{ padding: '$4', minWidth: '200px' }}>
                        <Row align="center" css={{ gap: '$4', marginBottom: '$4' }}>
                          <Avatar text={user.name.charAt(0)} size="lg" bordered color={user.status === 'online' ? 'success' : 'default'} />
                          <Col>
                            <Text b>{user.name}</Text>
                            <Text size="$sm" color={user.status === 'online' ? 'success' : 'error'}>{user.status === 'online' ? 'Online' : 'Offline'}</Text>
                          </Col>
                        </Row>
                        <Text size="$sm">System: <b>{user.systemStatus === 'logged_in' ? 'Logged In' : 'Logged Out'}</b></Text>
                        <Text size="$sm">Activity: <b>{user.activityStatus}</b></Text>
                        <Spacer y={0.5} />
                        <Text b size="$sm">Technical Details</Text>
                        <Text size="$sm">Browser: {user.browser}</Text>
                        <Text size="$sm">OS: {user.os}</Text>
                        <Spacer y={1} />
                        <Button size="sm" auto icon={<FaComment />} color="primary">
                          Message {user.unreadMessages > 0 ? `(${user.unreadMessages})` : ''}
                        </Button>
                      </Popover.Content>
                    </Popover>
                  ))}
                </Card.Body>
              </Card>
            </Grid>
          ))}
        </Grid.Container>

        {currentGroups.length === 0 && (
          <Row justify="center">
            <Text>No users found matching your criteria.</Text>
          </Row>
        )}

        <Spacer y={2} />

        {/* Pagination */}
        {totalPages > 0 && (
          <Row justify="center">
            <Pagination
              total={totalPages}
              initialPage={1}
              page={currentPage}
              onChange={setCurrentPage}
            />
          </Row>
        )}
      </div>
    </Container>
  );
};

export default UserMonitoringPage;